import React, { useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import CartItems from './CartItems';
import Voucher from './Voucher';
import Share from '../Favourites/Share';
import Button from '../Elements/Button/Button';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import { purchase } from '../../actions/checkout';
import { hasClass, removeClass } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { paymentMethodsMapping } from '../../config';
import s from './Cart.scss';

const Cart = ({ checkout, setup, maxHeight, favourites = false, favouriteItems, toggleCartHandler, ...props }) => {
	const { items = [], totals = {}, paymentMethods = [] } = checkout.selection;
	const intentsRendered = useRef(false);
	const closeCart = () => {
		if (hasClass(document.body, 'u_addedToBag')) {
			removeClass(document.body, 'u_addedToBag');
		} else {
			toggleCartHandler();
		}
	};

	useEffect(() => {
		if (items.length > 0 && !favourites && !intentsRendered.current) {
			const hasStripePaymentIntentsLoaded = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
			if (Object.keys(hasStripePaymentIntentsLoaded).length > 0) {
				props.purchase('selection', true, paymentMethodsMapping.stripePaymentIntents);
				intentsRendered.current = true;
			}
		}
	}, [items]);

	const hasStripePaymentIntents = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;

	let isLong = items.length > 0 && (items.length * 178) + 223 > maxHeight;
	if (favourites) isLong = favouriteItems.length > 0 && (favouriteItems.length * 178) + 67 > maxHeight;

	return (
		<div className={s.cart} style={{ height: `${maxHeight}px` }}>
			<div className={s.cart_header}>
				{ favourites && <span className={s.cart_header_title}>{ translate(setup, 'favouritesDrawerTitle') }</span> }
				<a className={s.cart_header_close} href="#close-cart" onClick={toggleCartHandler}>
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M13 1L1 13" stroke="black" />
						<path d="M1 1L13 13" stroke="black" />
					</svg>
				</a>
			</div>
			<div className={[s.cart_items, (favourites ? s.cart_items__favourites : ''), (isLong ? s.cart_items__long : '')].join(' ')}>
				<ErrorBoundary message="CartItems:" debugData={items}>
					<CartItems favourites={favourites} toggleCartHandler={toggleCartHandler} />
					{ favourites.length > 0 && <Share setup={setup} link={`${prefix(setup, `/favourites/${favouriteItems.join(',')}`)}`} viewLinkHandler={closeCart} view /> }
				</ErrorBoundary>
			</div>
			{
				items.length > 0 && !favourites && (
					<div className={[s.cart_totals, items.length > 0 ? s.cart_totals_hasItems : null].join(' ')}>
						<div className={s.cart_totals_voucher}>
							<Voucher setup={setup} checkout={checkout} />
						</div>
						<div className={s.cart_totals_row}>
							<span className={s.cart_totals_row_left}>{ translate(setup, 'checkoutProductTotal') }</span>
							<span className={s.cart_totals_row_right}>{ totals.itemsTotalPrice }</span>
						</div>
						<div className={s.cart_totals_row}>
							<span className={s.cart_totals_row_left}>{ translate(setup, 'shipping') }</span>
							<span className={s.cart_totals_row_right}>{ totals.shippingPrice }</span>
						</div>
						{
							totals.totalDiscountPriceAsNumber !== false && (
								<div className={[s.cart_totals_row, s.cart_totals_row__discount].join(' ')}>
									<span className={s.cart_totals_row_left}>{ translate(setup, 'checkoutDiscount') }</span>
									<span className={s.cart_totals_row_right}>{ totals.totalDiscountPrice }</span>
								</div>
							)
						}
						<div className={[s.cart_totals_row, s.cart_totals_row__grandTotal].join(' ')}>
							<span className={s.cart_totals_row_left}>{ translate(setup, 'total') }</span>
							<span className={s.cart_totals_row_right}>{ totals.grandTotalPrice }</span>
						</div>
						<div className={s.cart_totals_button}>
							<Button variantClasses={['button__inverse']} type={checkout.klarnaLoaded ? 'a' : 'link'} location={prefix(setup, 'selection')} href={prefix(setup, 'selection')} clickEventHandler={closeCart}>
								{ translate(setup, 'goToCheckout') }
							</Button>
						</div>
						{
							Object.keys(hasStripePaymentIntents).length > 0 && (
								<div className={s.cart_totals_stripe}>
									<div id="stripePaymentIntentsContainer" />
									<div className={s.cart_totals_stripe_paymentButton} id="stripeCheckoutButton" />
									<span className={s.cart_totals_stripe_expressText}>{ translate(setup, 'cartExpress') }</span>
								</div>
							)
						}
					</div>
				)
			}
		</div>
	);
};

const mapState = ({ checkout, environment }, props) => {
	const { setup, header, favourites: favouriteItems } = environment;
	return { checkout, setup, header, favouriteItems, ...props };
};

export default hot(module)(connect(mapState, { purchase })(Cart));
